import {
  HouseholdItem, PEOPLE, Person, RECURRENCE_LABELS, Status, STATUS_LABELS,
} from './types';
import { daysUntil, isOverdue } from './householdUtils';

interface ItemCardProps {
  item: HouseholdItem;
  projectTitle?: string;
  projectColor?: string;
  // eslint-disable-next-line no-unused-vars
  onStatusChange: (status: Status) => void;
  // eslint-disable-next-line no-unused-vars
  onAssigneeChange: (assignee: Person | undefined) => void;
  onEdit: () => void;
  onDelete: () => void;
  onComplete: () => void;
}

function dueLabel(dueDate: string): { text: string; className: string } {
  const days = daysUntil(dueDate);
  if (days < 0) return { text: `Overdue by ${Math.abs(days)}d`, className: 'hh-due-overdue' };
  if (days === 0) return { text: 'Due today', className: 'hh-due-soon' };
  if (days === 1) return { text: 'Due tomorrow', className: 'hh-due-soon' };
  if (days <= 3) return { text: `Due in ${days}d`, className: 'hh-due-soon' };
  return { text: `Due ${dueDate}`, className: 'hh-due-normal' };
}

function ItemCard({
  item, projectTitle, projectColor,
  onStatusChange, onAssigneeChange, onEdit, onDelete, onComplete,
}: ItemCardProps) {
  const overdue = isOverdue(item);
  const due = item.dueDate ? dueLabel(item.dueDate) : undefined;

  return (
    <div
      className={`hh-card${overdue ? ' hh-card-overdue' : ''}`}
      style={projectColor ? { borderLeft: `3px solid ${projectColor}` } : undefined}
    >
      <div className="hh-card-top">
        {projectTitle && (
          <span className="hh-project-tag" style={{ backgroundColor: `${projectColor}33`, color: projectColor }}>
            {projectTitle}
          </span>
        )}
        {item.recurrence !== 'none' && (
          <span className="hh-recur-badge" title="Recurring">
            ↻
            {' '}
            {RECURRENCE_LABELS[item.recurrence]}
          </span>
        )}
      </div>

      <button type="button" className="hh-card-title" onClick={onEdit}>{item.title}</button>

      {item.description && <p className="hh-card-desc">{item.description}</p>}

      {due && <div className={`hh-due ${due.className}`}>{due.text}</div>}

      {item.effort !== undefined && (
        <div className="hh-effort" title="Effort score">
          Effort:
          {' '}
          {'●'.repeat(item.effort)}
          {'○'.repeat(5 - item.effort)}
        </div>
      )}

      <div className="hh-card-controls">
        <select
          value={item.status}
          onChange={(e) => onStatusChange(e.target.value as Status)}
        >
          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>

        <select
          value={item.assignee ?? ''}
          onChange={(e) => onAssigneeChange((e.target.value || undefined) as Person | undefined)}
        >
          <option value="">Unassigned</option>
          {PEOPLE.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <div className="hh-card-actions">
        {item.status !== 'done' && (
          <button type="button" className="hh-btn hh-btn-small hh-btn-primary" onClick={onComplete}>
            Mark done
          </button>
        )}
        <button type="button" className="hh-btn hh-btn-small hh-btn-secondary" onClick={onEdit}>Edit</button>
        <button type="button" className="hh-btn hh-btn-small hh-btn-danger" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

ItemCard.defaultProps = {
  projectTitle: undefined,
  projectColor: undefined,
};

export default ItemCard;
